import { Title } from '@solidjs/meta'
import { useNavigate, useParams, useSearchParams } from '@solidjs/router'
import { type Component, createEffect, createMemo, createResource, createSignal, onCleanup, Show } from 'solid-js'
import Card from '~/components/Card'
import Chip from '~/components/Chip'
import Container from '~/components/Container'
import GoBack from '~/components/GoBack'
import Tabs from '~/components/Tabs'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { usePermissions } from '~/hooks/usePermissions'
import UserCompaniesTab from '~/layers/ui/admin/usuarios/UserCompaniesTab'
import UserPermissionsTab from '~/layers/ui/admin/usuarios/UserPermissionsTab'
import UserProfileTab from '~/layers/ui/admin/usuarios/UserProfileTab'
import { companyRoleColor, companyRoleLabel, userInitials } from '~/services/access.service'
import { userService } from '~/services/user.service'
import { authStore } from '~/stores/auth'
import { setTrailOverride } from '~/stores/breadcrumb'
import type { TabItemType } from '~/types/Tabs'
import { resolveError } from '~/utils/error'
import { P } from '~/utils/permissions'

const TAB_IDS = ['perfil', 'permisos', 'empresas']

/**
 * Ficha completa de un usuario: sus datos de acceso, el rol y los permisos
 * sueltos que se le añaden por encima del rol, y las empresas a las que entra.
 *
 * Cada pestaña guarda por su cuenta contra su propio endpoint: mezclar perfil,
 * permisos y empresas en un único envío obligaba a recalcularlo todo aunque
 * sólo cambiara el nombre.
 */
const UsuarioDetallePage: Component = () => {
  const params = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const toast = useToast()
  const alert = useAlert()
  const { can, isRoot } = usePermissions()

  const userId = () => Number(params.id)
  const isSelf = () => userId() === authStore.user?.id

  const [user, { refetch }] = createResource(userId, async id => {
    try {
      return await userService.get(id)
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
      return null
    }
  })

  // Miga de pan propia: "Usuarios › [nombre]". Se limpia al salir de la ficha.
  createEffect(() => {
    const name = user()?.name
    if (name) setTrailOverride(['Usuarios', name])
  })
  onCleanup(() => setTrailOverride(null))

  // El título se precalcula: leer una condición dentro de <Title> crea un memo
  // fuera del root cuando solid-meta limpia la etiqueta.
  const pageTitle = createMemo(() => (user()?.name ? `${user()!.name} | Usuarios` : 'Usuario | Florez Jaramillo'))

  const [activeTab, setActiveTab] = createSignal(TAB_IDS.includes(String(searchParams.tab)) ? String(searchParams.tab) : 'perfil')
  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    setSearchParams({ ...searchParams, tab }, { replace: true })
  }

  const handleDelete = async () => {
    const ok = await alert.confirmDelete({
      title: 'Quitar usuario',
      message: `¿Quitar a ${user()?.name} de esta empresa? Si no pertenece a ninguna otra, se eliminará su cuenta.`
    })
    if (!ok) return
    try {
      await userService.delete(userId())
      toast.addToast({ title: 'Usuario eliminado', type: 'success' })
      navigate('/admin/usuarios')
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const canUpdate = can(P.users.update)
  const canAssign = can(P.users.assign)
  const canDelete = can(P.users.delete)

  // Sin `badge`: el Badge de la pestaña se posiciona sobre la etiqueta y se lee
  // pegado al texto ("Empresas1"). Los contadores van dentro de cada pestaña.
  const tabs = (): TabItemType[] => [
    {
      id: 'perfil',
      label: 'Perfil y acceso',
      icon: 'material-symbols-light:person-outline',
      content: <UserProfileTab user={user} userId={userId} onRefetch={refetch} isSelf={isSelf} isRoot={isRoot} canUpdate={canUpdate} canDelete={canDelete} onDelete={handleDelete} />
    },
    { id: 'permisos', label: 'Rol y permisos', icon: 'material-symbols-light:shield-outline', content: <UserPermissionsTab user={user} userId={userId} onRefetch={refetch} canAssign={canAssign} /> },
    { id: 'empresas', label: 'Empresas', icon: 'material-symbols-light:domain', content: <UserCompaniesTab user={user} userId={userId} onRefetch={refetch} isSelf={isSelf} canAssign={canAssign} /> }
  ]

  return (
    <Container>
      <Title>{pageTitle()}</Title>

      <GoBack label="Volver a usuarios" fallbackPath="/admin/usuarios" />

      <Show
        when={user() && !user.loading}
        fallback={
          <Card class="mt-3">
            <Card.Body>
              <div class="py-10 text-center text-sm text-foreground-subtle">{user.loading ? 'Cargando usuario...' : 'No se pudo cargar el usuario'}</div>
            </Card.Body>
          </Card>
        }
      >
        <div class="mt-3 mb-6 flex flex-wrap items-center gap-4">
          <span class="flex size-14 shrink-0 items-center justify-center rounded-full bg-primary-soft text-lg font-semibold text-primary">{userInitials(user()!.name)}</span>
          <div class="min-w-0">
            <h1 class="truncate text-2xl font-bold text-foreground">{user()!.name}</h1>
            <p class="truncate text-sm text-foreground-subtle">{user()!.email}</p>
          </div>
          <div class="flex flex-wrap items-center gap-2">
            <Chip variant="soft" color={companyRoleColor(user()!.companyRole)}>
              {companyRoleLabel(user()!.companyRole)}
            </Chip>
            <Chip variant="soft" color={user()!.status === 1 ? 'success' : 'default'}>
              {user()!.status === 1 ? 'Activo' : 'Inactivo'}
            </Chip>
            <Show when={user()!.isRoot}>
              <Chip variant="soft" color="danger" icon="material-symbols-light:shield-person-outline">
                Super administrador
              </Chip>
            </Show>
          </div>
        </div>

        <Tabs items={tabs()} activeTab={activeTab()} onChange={handleTabChange} variant="underline" transition="fade" showContent keepMounted />
      </Show>

      <alert.AlertComponent />
    </Container>
  )
}

export default UsuarioDetallePage
